// i18n
import 'src/locales/i18n';

// scrollbar
import 'simplebar-react/dist/simplebar.min.css';

// lightbox
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

// map
import 'mapbox-gl/dist/mapbox-gl.css';

// editor
import 'react-quill/dist/quill.snow.css';

// carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// image
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------

import * as Sentry from '@sentry/react';

// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// routes
import Router from 'src/routes/sections';
// theme
import ThemeProvider from 'src/theme';
// hooks
import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
// components
import ProgressBar from 'src/components/progress-bar';
import MotionLazy from 'src/components/animate/motion-lazy';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';
import { SettingsProvider, SettingsDrawer } from 'src/components/settings';
// auth
import { AuthProvider, AuthConsumer } from 'src/auth/context/jwt';
import { PushNotifications, PermissionStatus, Token, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

import { initializeApp } from "firebase/app";
import { setupIonicReact } from '@ionic/react';
import creds from './api/creds';
import {serverTimeNow} from "./utils/servertimenow";

setupIonicReact();

serverTimeNow();

async function setupPushNotifications() {
  const firebaseConfig = {
    apiKey: "AIzaSyB4cXKIuLsB2bWwPxH5LLO3HJwerCAAXsI",
    authDomain: "matematico-1.firebaseapp.com",
    projectId: "matematico-1",
    storageBucket: "matematico-1.appspot.com",
    messagingSenderId: "811642015951",
    appId: "1:811642015951:web:f97f1c93f051d76c5ce7ba",
    measurementId: "G-7P16GHNBXV"
  };

  const LS_DECLINED_PUSH_NOTIFICATIONS_TIMESTAMP = 'declinedPushNotificationsTimestamp';
  const declinedPushNotificationsTimestamp = localStorage.getItem(LS_DECLINED_PUSH_NOTIFICATIONS_TIMESTAMP);

  // Initialize Firebase
  const app = initializeApp(firebaseConfig);
  const token = creds.data.getPushNotifToken();
  try {
    if (!token && !declinedPushNotificationsTimestamp) {
      await PushNotifications.unregister();
    }
  } catch (e) {
    Sentry.captureException(e);
  }

  if (declinedPushNotificationsTimestamp) {
    if (new Date().getTime() - Number(declinedPushNotificationsTimestamp) > 1000 * 60 * 60 * 24 * 7) {
      localStorage.removeItem(LS_DECLINED_PUSH_NOTIFICATIONS_TIMESTAMP);
      Sentry.captureMessage('Push notifications were declined for more than 7 days. Retrying...');
    } else {
      Sentry.captureMessage('Push notifications is declined and we have more rime to wait. Skipping...');
      return;
    }
  }

  console.log('Firebase initialized');

  try {
    // Request Permissions
    const permission: PermissionStatus = await PushNotifications.requestPermissions();
    if (permission.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      await PushNotifications.register();
    } else {
      localStorage.setItem(LS_DECLINED_PUSH_NOTIFICATIONS_TIMESTAMP, new Date().getTime().toString());
    }

    // Registration in firebase
    PushNotifications.addListener(
      'registration',
      async (t: Token) => {
        creds.setPushNotifToken(t.value);
      }
    );

    // Subscribed notifications
    PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        Sentry.captureMessage(`Push notification received: ${JSON.stringify(notification)}`);
        console.log(`Push received: ${JSON.stringify(notification)}`);
      }
    );
  } catch (e) {
    Sentry.captureException(e);
  }
}

if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
  setupPushNotifications().catch((err) => {
    Sentry.captureException(err);
  });
}

// ----------------------------------------------------------------------
export default function App() {
  useScrollToTop();

  return (
    <AuthProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SettingsProvider
          defaultSettings={{
            themeMode: 'light', // 'light' | 'dark'
            themeDirection: 'ltr', //  'rtl' | 'ltr'
            themeContrast: 'default', // 'default' | 'bold'
            themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
            themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
            themeStretch: false,
          }}
        >
          <ThemeProvider>
            <MotionLazy>
              <SnackbarProvider>
                  <SettingsDrawer />
                  <ProgressBar />
                  <AuthConsumer>
                    <Router />
                  </AuthConsumer>
              </SnackbarProvider>
            </MotionLazy>
          </ThemeProvider>
        </SettingsProvider>
      </LocalizationProvider>
      {/* <SplashScreen></SplashScreen> */}
    </AuthProvider>
  );
}
